import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Sparkles, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const BAND_LABEL = { economy: "Economy", mid: "Media", premium: "Premium" };

export default function ExperienceOptIn() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/business/experience-config").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <div className="text-zinc-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /> Caricamento…</div>;

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggle = (k, val) => setCfg((c) => ({ ...c, [k]: c[k].includes(val) ? c[k].filter((x) => x !== val) : [...c[k], val] }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/business/experience-config", {
        experience_optin: cfg.experience_optin, experience_occasions: cfg.experience_occasions,
        experience_price_band: cfg.experience_price_band, experience_categories: cfg.experience_categories,
        experience_availability_mode: cfg.experience_availability_mode, experience_availability_days: cfg.experience_availability_days,
        experience_availability_from: cfg.experience_availability_from, experience_availability_to: cfg.experience_availability_to,
        experience_recommended_products: cfg.experience_recommended_products,
      });
      toast.success("Impostazioni Esperienze AI salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const dim = !cfg.experience_optin ? "opacity-50 pointer-events-none" : "";

  return (
    <div className="space-y-5 max-w-2xl" data-testid="business-experience-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#7DF9FF]/15 border border-[#7DF9FF]/30 flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#7DF9FF]" /></div>
        <div><h2 className="font-display text-xl font-bold">Esperienze AI</h2><p className="text-sm text-zinc-400">Rendi i tuoi prodotti componibili dall'AI Concierge nelle esperienze dei clienti.</p></div>
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5 flex items-center justify-between">
        <div><div className="font-semibold">Partecipa alle Esperienze AI</div><div className="text-sm text-zinc-400">Se attivo, i tuoi prodotti disponibili possono entrare nelle esperienze combinate.</div></div>
        <Switch data-testid="exp-optin-toggle" checked={cfg.experience_optin} onCheckedChange={(v) => set("experience_optin", v)} />
      </div>

      <div className={`space-y-5 transition-opacity ${dim}`}>
        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <div className="font-semibold mb-2">Occasioni adatte</div>
          <div className="flex flex-wrap gap-2">
            {(cfg.occasions || []).map((o) => (
              <button key={o.key} data-testid={`exp-occ-${o.key}`} onClick={() => toggle("experience_occasions", o.key)}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${cfg.experience_occasions.includes(o.key) ? "bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40" : "bg-white/5 text-zinc-300 border-white/15"}`}>
                {o.emoji} {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <div className="font-semibold mb-1">Categorie disponibili</div>
          <p className="text-xs text-zinc-500 mb-2">Quali categorie di prodotti possono entrare nelle esperienze. Vuoto = tutte.</p>
          <div className="flex flex-wrap gap-2">
            {(cfg.categories || []).length === 0 && <span className="text-xs text-zinc-500">Nessuna categoria: aggiungi prodotti dal Menu.</span>}
            {(cfg.categories || []).map((c) => (
              <button key={c} data-testid={`exp-cat-${c}`} onClick={() => toggle("experience_categories", c)}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${cfg.experience_categories.includes(c) ? "bg-[#7DF9FF]/15 text-[#7DF9FF] border-[#7DF9FF]/40" : "bg-white/5 text-zinc-300 border-white/15"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <div className="font-semibold mb-2">Fascia di prezzo</div>
          <div className="flex gap-2">
            {(cfg.price_bands || []).map((b) => (
              <button key={b} data-testid={`exp-band-${b}`} onClick={() => set("experience_price_band", b)}
                className={`text-sm rounded-full px-4 py-1.5 border ${cfg.experience_price_band === b ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>
                {BAND_LABEL[b] || b}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <div className="font-semibold mb-2">Disponibilità</div>
          <div className="flex gap-2 mb-3">
            {[["always", "Sempre"], ["scheduled", "Fascia oraria"]].map(([m, l]) => (
              <button key={m} data-testid={`exp-avail-${m}`} onClick={() => set("experience_availability_mode", m)}
                className={`text-sm rounded-full px-4 py-1.5 border ${cfg.experience_availability_mode === m ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>
                {l}
              </button>
            ))}
          </div>
          {cfg.experience_availability_mode === "scheduled" && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {(cfg.weekdays || []).map((w) => (
                  <button key={w.key} data-testid={`exp-day-${w.key}`} onClick={() => toggle("experience_availability_days", w.key)}
                    className={`text-xs rounded-lg px-3 py-1.5 border ${cfg.experience_availability_days.includes(w.key) ? "bg-[#7DF9FF]/15 text-[#7DF9FF] border-[#7DF9FF]/40" : "bg-white/5 text-zinc-300 border-white/15"}`}>
                    {w.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-400">Dalle</span>
                <Input data-testid="exp-time-from" type="time" value={cfg.experience_availability_from} onChange={(e) => set("experience_availability_from", e.target.value)} className="bg-[#0A0A0C] border-white/10 w-32" />
                <span className="text-zinc-400">alle</span>
                <Input data-testid="exp-time-to" type="time" value={cfg.experience_availability_to} onChange={(e) => set("experience_availability_to", e.target.value)} className="bg-[#0A0A0C] border-white/10 w-32" />
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><Star className="h-4 w-4 text-[#FFD700]" /> Prodotti consigliati</div>
          <p className="text-xs text-zinc-500 mb-3">L'AI darà priorità a questi prodotti quando compone le esperienze.</p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {(cfg.products || []).length === 0 && <span className="text-xs text-zinc-500">Nessun prodotto. Aggiungine dal Menu.</span>}
            {(cfg.products || []).map((p) => (
              <label key={p.id} data-testid={`exp-rec-${p.id}`} className="flex items-center gap-2.5 rounded-lg bg-[#0A0A0C] border border-white/10 px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={cfg.experience_recommended_products.includes(p.id)} onChange={() => toggle("experience_recommended_products", p.id)} className="h-4 w-4 accent-[#FFD700]" />
                <span className="text-sm flex-1 truncate">{p.name}</span>
                <span className="text-xs text-zinc-500">{p.category}</span>
                {!p.available && <span className="text-[10px] text-red-400">non disp.</span>}
              </label>
            ))}
          </div>
        </div>
      </div>

      <Button data-testid="exp-config-save" onClick={save} disabled={busy} className="bg-[#00E676] text-black rounded-full">{busy ? "…" : "Salva impostazioni"}</Button>
    </div>
  );
}
